import { promptEngineerConfig } from './promptEngineerConstants';

export const createInitialPromptEngineerConfig = () => JSON.parse(JSON.stringify(promptEngineerConfig));

const hasMinOne = (arr) => Array.isArray(arr) && arr.length > 0;

const hasValidBusinessActionSelections = (config) => {
    const selections = config?.businessActionSelections;
    if (!Array.isArray(selections) || selections.length === 0) {
        return true;
    }

    const seenActionKeys = new Set();
    for (const selection of selections) {
        const actionKey = selection?.selectedBusinessActionKey?.trim?.();
        const serviceKey = selection?.selectedServiceKey?.trim?.();
        if (!actionKey || !serviceKey) {
            return false;
        }
        if (seenActionKeys.has(actionKey)) {
            return false;
        }
        seenActionKeys.add(actionKey);
    }

    return true;
};

export const isInstructionNameValid = (instructionName) => Boolean(instructionName?.trim());

export const isPhaseValid = (phaseIndex, config, instructionName = '') => {
    if (!config) return false;

    switch (phaseIndex) {
        case 0:
            return isInstructionNameValid(instructionName);
        case 1:
            return Boolean(config.business.industry)
                && Boolean(config.business.audience)
                && Boolean(config.business.description?.trim())
                && config.business.description.trim().length >= 20;
        case 2:
            return Boolean(config.personality.primary)
                && Boolean(config.personality.communicationStyle);
        case 3:
            return hasMinOne(config.behavior.selectedBehaviors)
                && Boolean(config.behavior.uncertaintyHandling)
                && Boolean(config.behavior.offTopicHandling);
        case 4:
            return Boolean(config.role.mainJob)
                && hasMinOne(config.role.tasks)
                && Boolean(config.role.taskApproach);
        case 5:
            return hasValidBusinessActionSelections(config);
        case 6:
            return hasMinOne(config.success.goals)
                && hasMinOne(config.success.prohibitedActions)
                && hasMinOne(config.success.escalationTriggers);
        default:
            return false;
    }
};

export const updatePromptEngineerField = (config, section, field, value) => ({
    ...config,
    [section]: {
        ...config[section],
        [field]: value
    }
});

export const updatePromptEngineerRootField = (config, field, value) => ({
    ...config,
    [field]: value
});

const listOrFallback = (arr, fallback = 'None specified.') => {
    if (!Array.isArray(arr) || arr.length === 0) return fallback;
    return arr.map((item) => `- ${item}`).join('\n');
};

export const buildPlaceholderInstructions = (config) => {
    if (!config) return '';

    const secondaryTone = config.personality.secondary
        ? ` with a ${config.personality.secondary} secondary style`
        : '';

    return [
        'Identity',
        `You are an AI assistant for a ${config.business.industry || 'business'} organization serving ${config.business.audience || 'its audience'}.`,
        '',
        'Industry & Audience',
        'Business description:',
        config.business.description || 'No business description provided.',
        '',
        'Primary Responsibilities',
        `Your main job is ${config.role.mainJob || 'to support users'} and help users with relevant needs based on company-approved guidance.`,
        '',
        'Communication Style',
        `Use a ${config.personality.primary || 'professional'} primary tone${secondaryTone}.`,
        `Preferred response style: ${config.personality.communicationStyle || 'Helpful and conversational'}.`,
        '',
        'Behavior Standards',
        listOrFallback(config.behavior.selectedBehaviors),
        '',
        'Knowledge Usage',
        '- Use only approved knowledge and provided context to answer user questions.',
        '- If details are missing, ask clarifying questions before providing a final recommendation.',
        '- Prioritize accurate and concise guidance aligned with the supported scope.',
        '',
        'Uncertainty Handling',
        config.behavior.uncertaintyHandling || 'No uncertainty handling strategy selected.',
        '',
        'Off-Topic Handling',
        config.behavior.offTopicHandling || 'No off-topic handling strategy selected.',
        '',
        'Core Tasks',
        listOrFallback(config.role.tasks),
        config.role.customTasks?.trim() ? `Additional custom tasks:\n${config.role.customTasks.trim()}` : '',
        '',
        'Task Approach',
        config.role.taskApproach || 'No task approach selected.',
        '',
        'Success Goals',
        listOrFallback(config.success.goals),
        '',
        'Prohibited Actions',
        listOrFallback(config.success.prohibitedActions),
        '',
        'Escalation Triggers',
        listOrFallback(config.success.escalationTriggers),
        '',
        'Closing Behavior',
        '- End responses with clear next steps when helpful.',
        '- Confirm if the user wants additional support before closing.',
        '- Offer escalation when needed based on the escalation trigger policy.'
    ].filter(Boolean).join('\n');
};

export const buildDefaultInstructionName = (config) => {
    const mainJob = config?.role?.mainJob?.trim();
    const industry = config?.business?.industry?.trim();
    const audience = config?.business?.audience?.trim();

    const parts = ['AI Prompt Engineer'];
    if (mainJob) {
        parts.push(mainJob);
    }
    if (industry) {
        parts.push(industry);
    }
    if (audience) {
        parts.push(audience);
    }

    return parts.join(' - ');
};
